/**
 * RC-MOBILE-AUTH-REPAIR-001 · Stage 1c wiring helpers.
 *
 * Minimal behaviour-preserving orchestration extracted from
 * AuthContext.jsx and AuthCallback.jsx so the native/web branching can
 * be tested executably without rendering the full provider tree.
 *
 * These helpers are the ONLY places the app performs:
 *   1. mobile-native bootGate before the initial /me probe,
 *   2. mobile-native handoff vs. web cookie exchange after Google
 *      returns to `/auth/callback`,
 *   3. mobile-native family revoke before the web /api/auth/logout
 *      call, followed by the single `cc:logout` DOM event.
 *
 * AuthContext.jsx and AuthCallback.jsx are the sole callers.
 */

/**
 * Runs the native install-identity check + attaches native axios
 * interceptors BEFORE the caller performs its first authenticated
 * request. On web this resolves immediately (bootGate is a no-op).
 */
export async function bootstrapNativeSession() {
  try {
    const mod = await import("@/lib/mobile/mobileAuthSession");
    await mod.bootGate();
  } catch {
    /* non-native or module missing → treat as no-op */
  }
}

/**
 * Completes the OAuth session exchange after Google redirects back to
 * `/auth/callback` with `#session_id=…`. Native uses the bearer-token
 * handoff (no cookies); web keeps the existing cookie-based
 * `/api/auth/google/session` endpoint.
 */
export async function runOAuthSessionExchange(sessionId) {
  const { isNativePlatform } = await import("@/lib/platform");
  if (isNativePlatform()) {
    // TKT #274563 · native Google signs in directly (nativeGoogleBroker → inline
    // /google/exchange with a Google id_token); it no longer returns through the
    // deep-link/AuthCallback leg. Reaching here on native is an unexpected legacy
    // callback — fail closed rather than exchange with the wrong contract.
    throw new Error("native_google_uses_direct_signin");
  } else {
    const { api } = await import("@/lib/api");
    await api.post("/api/auth/google/session", { session_id: sessionId });
  }
}

/**
 * Full logout sequence.
 *   1. Native: revoke the mobile session family + clear
 *      Keystore/Keychain tokens (best-effort).
 *   2. Web: `/api/auth/logout` (best-effort).
 *   3. Emit `cc:logout` exactly once.
 * The caller (AuthContext.logout) still owns `setUser(null)` after
 * this resolves.
 */
export async function performLogoutSequence() {
  const { isNativePlatform } = await import("@/lib/platform");
  let outcome;
  if (isNativePlatform()) {
    const mod = await import("@/lib/mobile/mobileAuthSession");
    outcome = await mod.logoutMobile();
  } else {
    const { api } = await import("@/lib/api");
    const response = await api.post("/api/auth/logout", {}, { _skipSessionTracking: true });
    outcome = response.data;
  }
  try {
    if (typeof window !== "undefined" && window.dispatchEvent) {
      window.dispatchEvent(new Event("cc:logout"));
    }
  } catch {
    /* non-DOM env */
  }
  return outcome;
}

/**
 * Sign-out after POST /api/account/delete. The server has already revoked
 * every session, so the logout call may 401 — local credentials must still
 * be cleared or the native app re-enters with the Keychain pair on reload.
 */
export async function performAccountDeletionSignOut() {
  let outcome = null;
  try {
    outcome = await performLogoutSequence();
  } catch {
    outcome = { ok: false, reason: "logout_after_revocation_failed" };
  }
  const { isNativePlatform } = await import("@/lib/platform");
  if (isNativePlatform()) {
    const mod = await import("@/lib/mobile/mobileAuthSession");
    await mod.clearMobileSession();
  }
  return outcome;
}
